export interface TimezoneOption {
  value: string;
  label: string;
  language?: string;
}

export interface LanguageTimezoneMap {
  [key: string]: string;
}

// Language to timezone mapping
export const LANGUAGE_TIMEZONE_MAP: LanguageTimezoneMap = {
  vi: "Asia/Ho_Chi_Minh", // UTC+7 (Vietnam)
  en: "America/New_York", // UTC-5 (US East)
  fr: "Europe/Paris", // UTC+1 (France)
  es: "Europe/Madrid", // UTC+1 (Spain)
  de: "Europe/Berlin", // UTC+1 (Germany)
  it: "Europe/Rome", // UTC+1 (Italy)
  pt: "Europe/Lisbon", // UTC+0 (Portugal)
  ja: "Asia/Tokyo", // UTC+9 (Japan)
  ko: "Asia/Seoul", // UTC+9 (Korea)
  zh: "Asia/Shanghai", // UTC+8 (China)
};

// Get UTC offset for a timezone
const getTimezoneOffset = (timezone: string): string => {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "shortOffset",
    });
    const parts = formatter.formatToParts(now);
    const offsetPart = parts.find((part) => part.type === "timeZoneName");
    if (offsetPart) {
      return offsetPart.value;
    }
    // Fallback: calculate offset manually
    const utcDate = new Date(now.toLocaleString("en-US", { timeZone: "UTC" }));
    const tzDate = new Date(now.toLocaleString("en-US", { timeZone: timezone }));
    const offset = (tzDate.getTime() - utcDate.getTime()) / (1000 * 60);
    const hours = Math.floor(Math.abs(offset) / 60);
    const minutes = Math.abs(offset) % 60;
    const sign = offset >= 0 ? "+" : "-";
    return `UTC${sign}${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  } catch {
    return "";
  }
};

// Get city name from timezone
const getCityName = (timezone: string): string => {
  const parts = timezone.split("/");
  if (parts.length > 1) {
    return parts[parts.length - 1].replace(/_/g, " ");
  }
  return timezone;
};

const getLanguagesByTimezone = (timezone: string): string[] => {
  return Object.keys(LANGUAGE_TIMEZONE_MAP).filter(
    (lang) => LANGUAGE_TIMEZONE_MAP[lang] === timezone
  );
};

// Generate timezone options from LANGUAGE_TIMEZONE_MAP
const generateTimezoneOptions = (): TimezoneOption[] => {
  try {
    // Get unique timezones from LANGUAGE_TIMEZONE_MAP
    const uniqueTimezones = Array.from(new Set(Object.values(LANGUAGE_TIMEZONE_MAP)));

    return uniqueTimezones
      .map((timezone) => {
        const offset = getTimezoneOffset(timezone);
        const city = getCityName(timezone);
        const languages = getLanguagesByTimezone(timezone);
        const language = languages.length > 0 ? languages[0] : undefined;
        return {
          value: timezone,
          label: offset ? `${offset} (${city})` : city,
          language,
        };
      })
      .sort((a, b) => a.value.localeCompare(b.value));
  } catch {
    return [
      { value: "Asia/Ho_Chi_Minh", label: "UTC+7 (Saigon)", language: "vi" },
      { value: "America/New_York", label: "UTC-5 (New York)", language: "en" },
      { value: "Europe/Paris", label: "UTC+1 (Paris)", language: "fr" },
      { value: "Europe/Madrid", label: "UTC+1 (Madrid)", language: "es" },
      { value: "Europe/Berlin", label: "UTC+1 (Berlin)", language: "de" },
      { value: "Europe/Rome", label: "UTC+1 (Rome)", language: "it" },
      { value: "Europe/Lisbon", label: "UTC+0 (Lisbon)", language: "pt" },
      { value: "Asia/Tokyo", label: "UTC+9 (Tokyo)", language: "ja" },
      { value: "Asia/Seoul", label: "UTC+9 (Seoul)", language: "ko" },
      { value: "Asia/Shanghai", label: "UTC+8 (Shanghai)", language: "zh" },
    ];
  }
};

export const TIMEZONE_OPTIONS: TimezoneOption[] = generateTimezoneOptions();

// Language selection options
export const LANGUAGE_OPTIONS: TimezoneOption[] = [
  { value: "en", label: "English" },
  { value: "vi", label: "Tiếng Việt" },
  { value: "fr", label: "Français" },
  { value: "es", label: "Español" },
  { value: "de", label: "Deutsch" },
  { value: "it", label: "Italiano" },
  { value: "pt", label: "Português" },
  { value: "ja", label: "日本語" },
  { value: "ko", label: "한국어" },
  { value: "zh", label: "中文" },
];

// Utility functions
export const getTimezoneByLanguage = (language: string): string => {
  return LANGUAGE_TIMEZONE_MAP[language] || Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const getTimezoneOptions = (): TimezoneOption[] => {
  return TIMEZONE_OPTIONS;
};

export const getLanguageOptions = (): TimezoneOption[] => {
  return LANGUAGE_OPTIONS;
};

export const getCurrentLanguage = (): string => {
  return localStorage.getItem("i18nextLng") || "en";
};

export const getDefaultTimezone = (): string => {
  const currentLanguage = getCurrentLanguage();
  return getTimezoneByLanguage(currentLanguage);
};
